/**
 * 사업자 크레딧 결제 필터 — 검색어/상태/결제수단/기간/정렬/페이지 사이즈.
 */
import { useEffect, useState } from 'react';
import type {
  CreditPaymentFilters as CreditPaymentFiltersType,
  CreditPaymentStatusFilter,
} from '../../types/payment';

interface Props {
  filters: CreditPaymentFiltersType;
  onFilterChange: (filters: CreditPaymentFiltersType) => void;
  pageSize: number;
  onPageSizeChange: (nextSize: number) => void;
}

export function CreditPaymentFilters({
  filters,
  onFilterChange,
  pageSize,
  onPageSizeChange,
}: Props) {
  const [localKeyword, setLocalKeyword] = useState(filters.searchKeyword || '');
  const [localStatus, setLocalStatus] = useState<CreditPaymentStatusFilter>(filters.paymentStatus ?? 'ALL');
  const [localMethod, setLocalMethod] = useState<string>(filters.paymentMethod ?? 'ALL');
  const [localStartDate, setLocalStartDate] = useState(filters.startDate || '');
  const [localEndDate, setLocalEndDate] = useState(filters.endDate || '');
  const [localSortBy, setLocalSortBy] = useState<'dtPaid' | 'payAmount'>(filters.sortBy ?? 'dtPaid');
  const [localSortOrder, setLocalSortOrder] = useState<'ASC' | 'DESC'>(filters.sortOrder ?? 'DESC');

  useEffect(() => {
    setLocalKeyword(filters.searchKeyword || '');
    setLocalStatus(filters.paymentStatus ?? 'ALL');
    setLocalMethod(filters.paymentMethod ?? 'ALL');
    setLocalStartDate(filters.startDate || '');
    setLocalEndDate(filters.endDate || '');
    setLocalSortBy(filters.sortBy ?? 'dtPaid');
    setLocalSortOrder(filters.sortOrder ?? 'DESC');
  }, [filters]);

  const handleSearch = () => {
    onFilterChange({
      searchKeyword: localKeyword || undefined,
      paymentStatus: localStatus,
      paymentMethod: localMethod === 'ALL' ? undefined : localMethod,
      startDate: localStartDate || undefined,
      endDate: localEndDate || undefined,
      sortBy: localSortBy,
      sortOrder: localSortOrder,
    });
  };

  const handleReset = () => {
    setLocalKeyword('');
    setLocalStatus('ALL');
    setLocalMethod('ALL');
    setLocalStartDate('');
    setLocalEndDate('');
    setLocalSortBy('dtPaid');
    setLocalSortOrder('DESC');
    onFilterChange({
      paymentStatus: 'ALL',
      sortBy: 'dtPaid',
      sortOrder: 'DESC',
    });
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
        <div className="lg:col-span-2">
          <label className="block text-xs font-medium text-gray-600 mb-1">검색</label>
          <input
            type="text"
            value={localKeyword}
            onChange={(e) => setLocalKeyword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="사업자명 / 원장명 / 주문번호"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">상태</label>
          <select
            value={localStatus}
            onChange={(e) => setLocalStatus(e.target.value as CreditPaymentStatusFilter)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            <option value="ALL">전체</option>
            <option value="COMPLETED">완료</option>
            <option value="CANCELLED">취소</option>
            <option value="REFUNDED">환불</option>
            <option value="PARTIAL_REFUNDED">부분 환불</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">결제 수단</label>
          <select
            value={localMethod}
            onChange={(e) => setLocalMethod(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            <option value="ALL">전체</option>
            <option value="카드">카드</option>
            <option value="계좌이체">계좌이체</option>
            <option value="간편결제">간편결제</option>
            <option value="가상계좌">가상계좌</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">시작일</label>
          <input
            type="date"
            value={localStartDate}
            onChange={(e) => setLocalStartDate(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">종료일</label>
          <input
            type="date"
            value={localEndDate}
            onChange={(e) => setLocalEndDate(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>
      </div>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <select
            value={localSortBy}
            onChange={(e) => setLocalSortBy(e.target.value as 'dtPaid' | 'payAmount')}
            className="rounded-md border border-gray-300 px-2 py-1.5 text-xs"
          >
            <option value="dtPaid">결제일시</option>
            <option value="payAmount">결제금액</option>
          </select>
          <select
            value={localSortOrder}
            onChange={(e) => setLocalSortOrder(e.target.value as 'ASC' | 'DESC')}
            className="rounded-md border border-gray-300 px-2 py-1.5 text-xs"
          >
            <option value="DESC">내림차순</option>
            <option value="ASC">오름차순</option>
          </select>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="rounded-md border border-gray-300 px-2 py-1.5 text-xs"
          >
            <option value={10}>10개</option>
            <option value={30}>30개</option>
            <option value={50}>50개</option>
            <option value={100}>100개</option>
          </select>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleReset}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
          >
            초기화
          </button>
          <button
            type="button"
            onClick={handleSearch}
            className="rounded-md bg-primary-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-600"
          >
            검색
          </button>
        </div>
      </div>
    </div>
  );
}
